import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SupervisorReviewForm = ({ caseId, review, agent, onUpdate, isAdmin }) => {
  const [data, setData] = useState(review || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setData(review || {}); }, [review]);

  const save = async (status) => {
    setSaving(true);
    try {
      const supId = agent?.id || 'admin';
      const supName = encodeURIComponent(agent?.name || 'Admin');
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/supervisor-review?supervisor_id=${supId}&supervisor_name=${supName}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, ...(status ? { status } : {}) })
      });
      onUpdate();
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="supervisor-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Supervisor Review</CardTitle>
        <span className={`px-3 py-1 rounded-full text-sm ${
          data.status === 'approved' ? 'bg-green-100 text-green-700' :
          data.status === 'rejected' ? 'bg-red-100 text-red-700' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {data.status?.replace(/_/g, ' ') || 'pending'}
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isAdmin && (
          <p className="text-sm text-blue-700 bg-blue-50 p-3 rounded">Only the admin/supervisor can mark this case approved or rejected.</p>
        )}
        <div className="grid md:grid-cols-2 gap-2">
          {[
            ['fact_find_reviewed', 'Fact-Find reviewed'],
            ['needs_analysis_reviewed', 'Needs Analysis reviewed'],
            ['recommendation_reviewed', 'Recommendation reviewed'],
            ['application_reviewed', 'Application reviewed'],
            ['replacement_reviewed', 'Replacement reviewed'],
            ['illustration_reviewed', 'Illustration reviewed'],
            ['quote_comparison_reviewed', 'Quote comparison reviewed'],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2">
              <input type="checkbox" disabled={!isAdmin} checked={data[k] || false} onChange={(e) => setData({ ...data, [k]: e.target.checked })} data-testid={`sup-${k}`} />
              <span className="text-sm">{l}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Supervisor Notes</label>
          <textarea rows="3" disabled={!isAdmin} value={data.notes || ''} onChange={(e) => setData({ ...data, notes: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="sup-notes" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Revision Notes (if needs revision)</label>
          <textarea rows="2" disabled={!isAdmin} value={data.revision_notes || ''} onChange={(e) => setData({ ...data, revision_notes: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="sup-revision-notes" />
        </div>
        {isAdmin && (
          <div className="flex gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => save()} disabled={saving} data-testid="sup-save-btn">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
            <Button className="bg-yellow-600 hover:bg-yellow-700" onClick={() => save('revision_requested')} disabled={saving} data-testid="sup-revise-btn">
              Request Revision
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => save('rejected')} disabled={saving} data-testid="sup-reject-btn">
              Reject
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => save('approved')} disabled={saving} data-testid="sup-approve-btn">
              <CheckCircle className="h-4 w-4 mr-1" />Approve
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================
// Signature Requests / E-Sign
// =============================================================
